
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import Attachment from "./Attachment";
import AlbumGrid from "./AlbumGrid";
import MessageActionsMenu from "./MessageActionsMenu";
import MessageEntityCard from "./MessageEntityCard";
import SiteMediaViewer from "../../components/media/SiteMediaViewer";
// Задачи есть не во всех проектах: окно «В задачу» берём из настроек чата проекта.
import { TodoFromMessageModal } from "./chatPlatform";
import { toggleMessageReaction } from "../../actions/message";
import { getMyReaction, normalizeReactions } from "../../utils/messageReactions";
import {
    downloadBlob,
    fetchChatAttachment,
    fetchChatAttachmentForView,
    getChatAttachmentName,
    getChatAttachmentMimeType,
    getChatAttachmentType,
    hasSecureChatAttachment,
    shareBlobFile,
} from "../../utils/chatAttachmentStore";
import { ALBUM_MAX, getAlbumId, sortAlbum } from "../../utils/chatAlbums";

const getId = (value) => String(value?._id || value || "");

const getForwardedSenderName = (message) => {
    const forwardedFrom = message?.forwardedFrom;
    if (!forwardedFrom || typeof forwardedFrom !== "object") return "";

    if (forwardedFrom.senderName) return String(forwardedFrom.senderName).trim();

    const sender = forwardedFrom.sender;
    if (sender && typeof sender === "object") {
        const name = [sender.firstName, sender.lastName].filter(Boolean).join(" ").trim();
        return name || sender.email || sender.username || "";
    }

    return "";
};

const getForwardedInfo = (message, currentUserId, chats = []) => {
    const forwardedFrom = message?.forwardedFrom;
    if (!forwardedFrom || typeof forwardedFrom !== "object") return null;

    const forwarded = !!(
        getId(forwardedFrom.messageId) ||
        getId(forwardedFrom.chatId) ||
        getId(forwardedFrom.sender) ||
        forwardedFrom.senderName
    );

    if (!forwarded) return null;

    const senderId = getId(forwardedFrom.sender);
    const senderName = getForwardedSenderName(message);
    const fromMe = !!senderId && senderId === getId(currentUserId);
    const authorText = fromMe ? "вас" : (senderName || "пользователя");

    const chatType = String(forwardedFrom.chatType || "");
    const isDirect = chatType === "direct" && forwardedFrom.isGroup !== true;
    const isSaved = chatType === "saved";
    const showSourceChat = !isDirect && !isSaved && !!forwardedFrom.chatTitle;
    const sourceChatId = getId(forwardedFrom.chatId);
    const sourceMessageId = getId(forwardedFrom.messageId);
    const sourceAccessible = !!sourceChatId && (chats || []).some(
        (chat) => getId(chat?._id) === sourceChatId
    );

    return {
        origin: forwardedFrom,
        authorText,
        showSourceChat,
        sourceTitle: String(forwardedFrom.chatTitle || "").trim(),
        sourceAccessible: sourceAccessible && !!sourceMessageId,
    };
};

const isInteractiveTarget = (target) => !!target?.closest?.(
    'button, a, input, textarea, select, label, [role="button"], [data-chat-no-select="true"]'
);

const canSelectMessage = (message) => !!(
    message?._id &&
    !String(message._id).startsWith("local:") &&
    !message?.isDeleted &&
    message?.type !== "call" &&
    !message?.call
);

const isViewableAttachment = (message) => (
    hasSecureChatAttachment(message) &&
    ["image", "video", "voice", "doc"].includes(getChatAttachmentType(message))
);

const getSenderName = (sender, currentUserId, guestSender = null) => {
    if (guestSender?.name) return guestSender.name;
    if (!sender) return "Собеседник";
    if (getId(sender) === getId(currentUserId)) return "Вы";

    const name = [sender.firstName, sender.lastName].filter(Boolean).join(" ").trim();

    return name || sender.email || sender.username || "Собеседник";
};

const formatTime = (value) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleTimeString("ru-RU", {
        hour: "2-digit",
        minute: "2-digit",
    });
};

const getDayKey = (value) => {
    const date = new Date(value || 0);
    if (Number.isNaN(date.getTime())) return "";
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

const formatDayLabel = (value) => {
    const date = new Date(value || 0);
    if (Number.isNaN(date.getTime())) return "";

    const now = new Date();
    const todayKey = getDayKey(now);
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);

    const key = getDayKey(date);
    if (key === todayKey) return "Сегодня";
    if (key === getDayKey(yesterday)) return "Вчера";

    return date.toLocaleDateString("ru-RU", {
        day: "numeric",
        month: "long",
        year: date.getFullYear() === now.getFullYear() ? undefined : "numeric",
    });
};

const formatCallStatus = (status) => {
    switch (status) {
        case "ringing": return "вызов";
        case "accepted": return "соединение установлено";
        case "missed": return "пропущен";
        case "ended": return "завершён";
        case "rejected": return "отклонён";
        case "busy": return "занято";
        case "failed": return "ошибка";
        case "cancelled": return "отменён";
        default: return status || "—";
    }
};

const parseLegacyReply = (text = "") => {
    if (!text.startsWith("↩️ ")) return null;

    const index = text.indexOf("\n");
    const head = index > -1 ? text.slice(0, index) : text;
    const body = index > -1 ? text.slice(index + 1) : "";
    const match = head.match(/^↩️\s+(.+?):\s+(.*)$/);

    return {
        author: match?.[1] || "Собеседник",
        snippet: match?.[2] || head.replace(/^↩️\s*/, ""),
        body,
    };
};

const getReply = (message) => {
    if (message?.replyTo?.messageId || message?.replyTo?.author || message?.replyTo?.snippet) {
        return {
            author: message.replyTo.author || "Собеседник",
            snippet: message.replyTo.snippet || "",
            body: message.text || "",
        };
    }

    return parseLegacyReply(message?.text || "");
};

const canGroupWithPrevious = (message, previous) => {
    if (!message || !previous) return false;
    if (message.type === "call" || message.call || previous.type === "call" || previous.call) return false;

    const currentSender = getId(message.sender) || `guest:${message.guestSender?.visitorId || message.guestSender?.publicId || ""}`;
    const previousSender = getId(previous.sender) || `guest:${previous.guestSender?.visitorId || previous.guestSender?.publicId || ""}`;
    if (!currentSender || currentSender !== previousSender) return false;

    const currentTime = new Date(message.createdAt || 0).getTime();
    const previousTime = new Date(previous.createdAt || 0).getTime();
    if (!currentTime || !previousTime) return false;

    return currentTime - previousTime >= 0 && currentTime - previousTime <= 5 * 60 * 1000;
};

const formatBytes = (value) => {
    const bytes = Number(value || 0);
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
    return `${(bytes / 1024 / 1024).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} МБ`;
};

const isLocalMessage = (message) => String(message?._id || "").startsWith("local:");

/**
 * Вложение, которое ещё отправляется (или не отправилось): фото и видео
 * видны сразу с компьютера, у файла — проценты загрузки.
 */
function LocalAttachment({ message }) {
    const type = getChatAttachmentType(message);
    const url = String(message?.__localUrl || "");
    const progress = Math.max(0, Math.min(100, Math.round(Number(message?.__uploadProgress || 0))));
    const sending = message?.__sendStatus === "sending";
    const note = message?.messageKind === "video_note";

    const progressNode = sending ? (
        <span className="chat-local-attachment-AO__progress">
            <i style={{ width: `${Math.max(4, progress)}%` }} />
            <small>{progress > 0 ? `${progress}%` : "Подготавливаем…"}</small>
        </span>
    ) : null;

    if ((type === "image" || type === "video") && url) {
        return (
            <div className={`chat-local-attachment-AO is-${note ? "note" : type}`}>
                {type === "image"
                    ? <img src={url} alt="" draggable={false} />
                    : <video src={url} muted playsInline preload="metadata" />}
                {progressNode}
            </div>
        );
    }

    return (
        <div className="chat-local-attachment-AO is-file">
            <span className="chat-local-attachment-AO__icon">{type === "voice" ? "🎙" : "▤"}</span>
            <span className="chat-local-attachment-AO__main">
                <b>{type === "voice" ? "Голосовое сообщение" : getChatAttachmentName(message)}</b>
                <small>{formatBytes(message?.fileSize) || "Файл"}</small>
            </span>
            {progressNode}
        </div>
    );
}

/**
 * Строки ленты дня: обычное сообщение — само по себе, идущие подряд фото
 * одного альбома (до 10) — одной строкой с сеткой.
 */
const buildRows = (messages = []) => {
    const rows = [];

    for (let index = 0; index < messages.length; index += 1) {
        const message = messages[index];
        const albumId = getAlbumId(message);

        if (albumId && getChatAttachmentType(message) === "image" && message.type !== "call") {
            const group = [message];
            while (group.length < ALBUM_MAX && index + 1 < messages.length) {
                const next = messages[index + 1];
                if (
                    getAlbumId(next) !== albumId ||
                    getId(next.sender) !== getId(message.sender) ||
                    getChatAttachmentType(next) !== "image"
                ) break;
                group.push(next);
                index += 1;
            }

            if (group.length > 1) {
                const album = sortAlbum(group);
                // Подпись и ответ лежат в одном из фото — оно и главное.
                const lead = album.find((item) => !!item.text || !!item.replyTo?.messageId) || album[0];
                rows.push({ message: lead, album });
                continue;
            }
        }

        rows.push({ message, album: null });
    }

    return rows;
};

const isGroupConversation = (chat) => {
    if (!chat) return false;
    if (chat.isGroup === true) return true;

    const chatType = String(chat.chatType || "direct").trim().toLowerCase();
    return !["direct", "legacy", "saved"].includes(chatType);
};

export default function ChatMessages({
    messages = [],
    currentUserId,
    selectedId,
    selectedMessageIds = [],
    selectionMode = false,
    chats = [],
    onReply,
    onEdit,
    onDelete,
    onDeleteRequest = null,
    onForward,
    onOpenForwardOrigin,
    onSelectMessage,
    onAddMessageSelection,
    onJumpToMessage = null,
    onRetry = null,
    onDiscard = null,
    /** Нажали на имя отправителя — открыть его профиль. */
    onOpenProfile = null,
    focusMessageId = null,
    officeId = null,
}) {
    const dispatch = useDispatch();
    const [openAttachmentId, setOpenAttachmentId] = useState(null);
    const [actionMenu, setActionMenu] = useState({ message: null, point: null });
    const [todoMessage, setTodoMessage] = useState(null);
    const rootRef = useRef(null);
    const longPressTimerRef = useRef(null);
    const longPressStartRef = useRef(null);
    const dragRef = useRef({ active: false, moved: false, pointerY: 0, startX: 0, startY: 0, startMessage: null });
    const dragRafRef = useRef(null);
    const suppressClickRef = useRef(false);
    const selectedSet = useMemo(() => new Set((selectedMessageIds || []).map(getId)), [selectedMessageIds]);
    const chatById = useMemo(() => new Map(
        (chats || [])
            .map((chat) => [getId(chat?._id), chat])
            .filter(([chatId]) => !!chatId)
    ), [chats]);

    const viewableMessages = useMemo(
        // Ещё не отправленные в просмотр не попадают: их нет на сервере.
        () => messages.filter((message) => isViewableAttachment(message) && !isLocalMessage(message)),
        [messages]
    );

    const dayGroups = useMemo(() => {
        const groups = [];

        (messages || []).forEach((message) => {
            const dayKey = getDayKey(message?.createdAt) || `unknown:${getId(message?._id)}`;
            const previousGroup = groups[groups.length - 1];

            if (!previousGroup || previousGroup.dayKey !== dayKey) {
                groups.push({
                    dayKey,
                    label: formatDayLabel(message?.createdAt),
                    messages: [message],
                });
                return;
            }

            previousGroup.messages.push(message);
        });

        return groups.map((group) => ({ ...group, rows: buildRows(group.messages) }));
    }, [messages]);

    /** Альбом по номеру любого его фото: отмечаем и выделяем альбом целиком. */
    const albumByMessageId = useMemo(() => {
        const map = new Map();
        dayGroups.forEach((group) => group.rows.forEach((row) => {
            if (!row.album) return;
            row.album.forEach((item) => map.set(getId(item._id), row.album));
        }));
        return map;
    }, [dayGroups]);

    const openAttachmentIndex = useMemo(() => {
        if (!openAttachmentId) return 0;

        const index = viewableMessages.findIndex(
            (message) => String(message?._id || "") === String(openAttachmentId)
        );

        return index >= 0 ? index : 0;
    }, [openAttachmentId, viewableMessages]);

    // Чертёж DXF открывается картинкой от сервера (с зумом), остальное — сам файл.
    const resolveAttachment = useCallback(
        (message) => fetchChatAttachmentForView({ message }),
        []
    );

    const handleAttachmentIndexChange = useCallback((_nextIndex, nextMessage) => {
        setOpenAttachmentId(nextMessage?._id || null);
    }, []);

    const downloadAttachment = useCallback(async (message) => {
        try {
            const asset = await fetchChatAttachment({ message, download: true, useCache: false });
            downloadBlob({
                blob: asset.blob,
                filename: asset.filename || getChatAttachmentName(message),
            });
        } catch (_error) {
            window.alert?.("Не удалось скачать файл. Проверьте связь и попробуйте ещё раз.");
        }
    }, []);

    const handleDownload = useCallback(async (message) => {
        const asset = await fetchChatAttachment({ message, download: true, useCache: false });
        downloadBlob({
            blob: asset.blob,
            filename: asset.filename || getChatAttachmentName(message),
        });
    }, []);

    const handleShare = useCallback(async (message) => {
        const asset = await fetchChatAttachment({ message });
        await shareBlobFile({
            blob: asset.blob,
            filename: asset.filename || getChatAttachmentName(message),
            mimeType: asset.mimeType || getChatAttachmentMimeType(message),
            title: "Файл из чата",
        });
    }, []);

    const closeActionMenu = useCallback(() => {
        setActionMenu({ message: null, point: null });
    }, []);

    const handleReaction = useCallback(async (message, emoji) => {
        if (!canSelectMessage(message) || !emoji) return null;

        try {
            return await dispatch(toggleMessageReaction(message._id, emoji));
        } catch (_error) {
            return null;
        }
    }, [dispatch]);

    const openActionMenu = useCallback((message, x, y) => {
        if (!message || !canSelectMessage(message)) return;

        setActionMenu({
            message,
            point: { x: Number(x || 0), y: Number(y || 0) },
        });
    }, []);

    const addMessageAtPoint = useCallback((clientX, clientY) => {
        const node = document.elementFromPoint(clientX, clientY)?.closest?.("[data-chat-message-id]");
        const messageId = node?.getAttribute?.("data-chat-message-id");
        if (!messageId) return;

        const album = albumByMessageId.get(getId(messageId));
        if (album) {
            album.filter(canSelectMessage).forEach((item) => onAddMessageSelection?.(item));
            return;
        }

        const message = (messages || []).find((item) => getId(item?._id) === getId(messageId));
        if (!canSelectMessage(message)) return;

        onAddMessageSelection?.(message);
    }, [albumByMessageId, messages, onAddMessageSelection]);

    const stopDragSelection = useCallback(() => {
        dragRef.current.active = false;
        dragRef.current.pointerY = 0;
        document.body.classList.remove("chat-drag-selecting-AO");

        if (dragRafRef.current) {
            window.cancelAnimationFrame(dragRafRef.current);
            dragRafRef.current = null;
        }
    }, []);

    const runAutoScroll = useCallback(() => {
        if (!dragRef.current.active || !dragRef.current.moved) {
            dragRafRef.current = null;
            return;
        }

        const scroller = rootRef.current?.closest?.(".chat-core-AO__messages");
        if (!scroller) {
            dragRafRef.current = null;
            return;
        }

        const rect = scroller.getBoundingClientRect();
        const y = dragRef.current.pointerY;
        const edge = Math.min(72, Math.max(42, rect.height * 0.12));
        let delta = 0;

        if (y < rect.top + edge) {
            delta = -Math.ceil((rect.top + edge - y) / 5);
        } else if (y > rect.bottom - edge) {
            delta = Math.ceil((y - (rect.bottom - edge)) / 5);
        }

        if (delta) {
            scroller.scrollTop += Math.max(-18, Math.min(18, delta));
            const pointerX = dragRef.current.pointerX || rect.left + rect.width / 2;
            addMessageAtPoint(pointerX, y);
        }

        dragRafRef.current = window.requestAnimationFrame(runAutoScroll);
    }, [addMessageAtPoint]);

    useEffect(() => {
        const handlePointerMove = (event) => {
            const drag = dragRef.current;
            if (!drag.active || event.pointerType !== "mouse") return;

            drag.pointerX = event.clientX;
            drag.pointerY = event.clientY;

            const distance = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);
            if (!drag.moved && distance >= 6) {
                drag.moved = true;
                suppressClickRef.current = true;
                document.body.classList.add("chat-drag-selecting-AO");
                if (drag.startMessage) {
                    const album = albumByMessageId.get(getId(drag.startMessage._id));
                    (album || [drag.startMessage]).filter(canSelectMessage).forEach((item) => onAddMessageSelection?.(item));
                }
            }

            if (!drag.moved) return;

            event.preventDefault();
            addMessageAtPoint(event.clientX, event.clientY);

            if (!dragRafRef.current) {
                dragRafRef.current = window.requestAnimationFrame(runAutoScroll);
            }
        };

        const handlePointerUp = () => stopDragSelection();

        document.addEventListener("pointermove", handlePointerMove, { passive: false });
        document.addEventListener("pointerup", handlePointerUp);
        document.addEventListener("pointercancel", handlePointerUp);

        return () => {
            document.removeEventListener("pointermove", handlePointerMove);
            document.removeEventListener("pointerup", handlePointerUp);
            document.removeEventListener("pointercancel", handlePointerUp);
            stopDragSelection();
        };
    }, [addMessageAtPoint, albumByMessageId, onAddMessageSelection, runAutoScroll, stopDragSelection]);

    useEffect(() => () => {
        if (longPressTimerRef.current) window.clearTimeout(longPressTimerRef.current);
    }, []);

    const handleMessagePointerDown = (event, message) => {
        if (!canSelectMessage(message) || isInteractiveTarget(event.target)) return;

        if (event.pointerType === "mouse") {
            if (event.button !== 0) return;

            // В истории чата ЛКМ отвечает за нашу drag-selection механику,
            // поэтому нативное синее выделение текста браузером не запускаем.
            event.preventDefault();

            dragRef.current = {
                active: true,
                moved: false,
                pointerX: event.clientX,
                pointerY: event.clientY,
                startX: event.clientX,
                startY: event.clientY,
                startMessage: message,
            };
            return;
        }

        if (selectionMode) return;

        longPressStartRef.current = { x: event.clientX, y: event.clientY };
        if (longPressTimerRef.current) window.clearTimeout(longPressTimerRef.current);

        longPressTimerRef.current = window.setTimeout(() => {
            suppressClickRef.current = true;
            openActionMenu(message, event.clientX, event.clientY);
            longPressTimerRef.current = null;
        }, 520);
    };

    const handleMessagePointerMove = (event) => {
        if (event.pointerType === "mouse" || !longPressTimerRef.current || !longPressStartRef.current) return;

        const distance = Math.hypot(
            event.clientX - longPressStartRef.current.x,
            event.clientY - longPressStartRef.current.y
        );

        if (distance > 10) {
            window.clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    };

    const handleMessagePointerEnd = () => {
        if (longPressTimerRef.current) {
            window.clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
        longPressStartRef.current = null;
    };

    if (!messages.length) {
        return (
            <div className="chat-messages-AO__empty">
                <span>Сообщений пока нет</span>
                <small>Первое сообщение появится здесь.</small>
            </div>
        );
    }

    return (
        // В режиме выбора нажатие по фото, файлу и карточке отмечает сообщение, а не открывает его.
        <div className={`chat-messages-AO ${selectionMode ? "is-selecting" : ""}`} ref={rootRef}>
            {dayGroups.map((group) => (
                <section className="chat-day-AO" key={group.dayKey}>
                    <div className="chat-date-AO"><span>{group.label}</span></div>

                    {group.rows.map((row, index) => {
                        const message = row.message;
                        const album = row.album;
                        const previous = index > 0 ? group.rows[index - 1].message : null;

                if (message.type === "call" || message.call) {
                    const call = message.call || {};
                    const outgoing = getId(call.initiator) === getId(currentUserId);

                    return (
                        <Fragment key={message._id}>
                            <div className={`chat-message-AO chat-message-AO--call ${outgoing ? "is-self" : "is-other"}`}>
                                <div className="chat-message-AO__call">
                                    <div className="chat-message-AO__call-icon">☎</div>
                                    <div>
                                        <div className="chat-message-AO__call-title">
                                            {outgoing ? "Исходящий" : "Входящий"} {call.video ? "видеозвонок" : "звонок"}
                                        </div>
                                        <div className="chat-message-AO__call-sub">
                                            {formatCallStatus(call.status)}
                                            {typeof call.durationSec === "number" && call.durationSec > 0
                                                ? ` · ${Math.floor(call.durationSec / 60)}:${String(call.durationSec % 60).padStart(2, "0")}`
                                                : ""}
                                        </div>
                                    </div>
                                </div>
                                <div className="chat-message-AO__meta">{formatTime(message.createdAt)}</div>
                            </div>
                        </Fragment>
                    );
                }

                const senderId = getId(message.sender);
                const isSelf = senderId === getId(currentUserId);
                const readByIds = Array.isArray(message.readBy) ? message.readBy.map(getId) : [];
                const readByOther = readByIds.some((id) => id && id !== getId(currentUserId));
                const reply = getReply(message);
                const bodyText = reply ? reply.body : message.text || "";
                const isFocused = focusMessageId && String(focusMessageId) === String(message._id);
                const isCard = ["product_card", "order_card"].includes(message.messageKind) || (message.entity?.type && message.entity.type !== "none");
                const hasAttachment = !!album || hasSecureChatAttachment(message);
                const attachmentType = album ? "image" : hasAttachment ? getChatAttachmentType(message) : "";
                // Своё вложение, которое ещё грузится (у обычного текста вложения нет).
                const local = !album && isLocalMessage(message) && hasSecureChatAttachment(message);
                // У альбома статус собирается со всех фото: одно не ушло — показываем это.
                const parts = album || [message];
                const failedPart = parts.find((part) => part?.__sendStatus === "failed") || null;
                const compact = canGroupWithPrevious(message, previous);
                const sendStatus = failedPart
                    ? "failed"
                    : parts.some((part) => part?.__sendStatus === "sending") ? "sending" : (message?.__sendStatus || null);
                const forwardedInfo = getForwardedInfo(message, currentUserId, chats);
                const messageChat = chatById.get(getId(message.chatId)) || null;
                const groupConversation = isGroupConversation(messageChat);
                const showSender = groupConversation && !isSelf && !compact;
                const mediaOnly = ["image", "video"].includes(attachmentType) &&
                    !bodyText && !reply && !forwardedInfo && !isCard;
                const cardOnly = isCard && !bodyText && !reply && !forwardedInfo && !hasAttachment;
                const voiceOnly = attachmentType === "voice" && !bodyText && !reply && !forwardedInfo && !isCard;
                const fileOnly = attachmentType === "doc" && !bodyText && !reply && !forwardedInfo && !isCard;
                const inlineMeta = !!bodyText && !hasAttachment && !isCard;
                const overlayMeta = mediaOnly || cardOnly;
                const bulkSelected = album
                    ? album.every((part) => selectedSet.has(getId(part._id)))
                    : selectedSet.has(getId(message._id));
                const reactions = normalizeReactions(message.reactions);
                const myReaction = getMyReaction(reactions, currentUserId);

                return (
                    <Fragment key={message._id}>
                        <div
                            data-chat-message-id={message._id}
                            className={[
                                "chat-message-row-AO",
                                isSelf ? "is-self" : "is-other",
                                bulkSelected ? "is-bulk-selected" : "",
                            ].filter(Boolean).join(" ")}
                            onContextMenu={(event) => {
                                // По фото, видео и файлу — тоже наше меню (а не меню браузера).
                                const onMedia = !!event.target?.closest?.(".attachment-AO");
                                if (!canSelectMessage(message) || (isInteractiveTarget(event.target) && !onMedia)) return;
                                event.preventDefault();
                                event.stopPropagation();
                                openActionMenu(message, event.clientX, event.clientY);
                            }}
                            onPointerDown={(event) => handleMessagePointerDown(event, message)}
                            onPointerMove={handleMessagePointerMove}
                            onPointerUp={handleMessagePointerEnd}
                            onPointerCancel={handleMessagePointerEnd}
                            onDragStart={(event) => event.preventDefault()}
                            onClick={(event) => {
                                if (suppressClickRef.current) {
                                    suppressClickRef.current = false;
                                    event.preventDefault();
                                    event.stopPropagation();
                                    return;
                                }

                                if (selectionMode && canSelectMessage(message) && !isInteractiveTarget(event.target)) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    onSelectMessage?.(album ? album.filter(canSelectMessage) : message);
                                }
                            }}
                        >
                            <div
                                id={`chat-message-${message._id}`}
                                className={[
                                    "chat-message-AO",
                                    isSelf ? "is-self" : "is-other",
                                    compact ? "is-compact" : "",
                                    selectedId === message._id ? "is-selected" : "",
                                    isFocused || (album && focusMessageId && album.some((part) => getId(part._id) === getId(focusMessageId))) ? "is-focused" : "",
                                    album ? "is-album" : "",
                                    failedPart ? "is-send-failed" : "",
                                    message.isDeleted ? "is-deleted" : "",
                                    isCard ? "is-card" : "",
                                    cardOnly ? "is-card-only" : "",
                                    mediaOnly ? "is-media-only" : "",
                                    voiceOnly ? "is-voice-only" : "",
                                    fileOnly ? "is-file-only" : "",
                                    showSender ? "has-sender-head" : "",
                                ].filter(Boolean).join(" ")}
                            >
                                {showSender && (
                                    <div className="chat-message-AO__head">
                                        {onOpenProfile && !selectionMode && !message.guestSender?.name && getId(message.sender) ? (
                                            // Имя — кнопка: открывает профиль отправителя.
                                            <button
                                                type="button"
                                                className="chat-message-AO__sender is-profile-link"
                                                onClick={() => onOpenProfile(message.sender)}
                                                title="Открыть профиль"
                                            >
                                                {getSenderName(message.sender, currentUserId, message.guestSender)}
                                            </button>
                                        ) : (
                                            <div className="chat-message-AO__sender">
                                                {getSenderName(message.sender, currentUserId, message.guestSender)}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {!!forwardedInfo && (
                                    <div className="chat-message-AO__forwarded">
                                        <span>Переслано от {forwardedInfo.authorText}</span>

                                        {forwardedInfo.showSourceChat && (
                                            forwardedInfo.sourceAccessible && onOpenForwardOrigin ? (
                                                <button
                                                    type="button"
                                                    data-chat-no-select="true"
                                                    title="Открыть исходное сообщение"
                                                    onClick={(event) => {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                        onOpenForwardOrigin(forwardedInfo.origin);
                                                    }}
                                                >
                                                    из «{forwardedInfo.sourceTitle}»
                                                </button>
                                            ) : (
                                                <span className="chat-message-AO__forwarded-source">
                                                    из «{forwardedInfo.sourceTitle}»
                                                </span>
                                            )
                                        )}
                                    </div>
                                )}

                                {reply && (
                                    // Нажали на цитату — переходим к сообщению, на которое ответили.
                                    <div
                                        className={`chat-message-AO__quote ${message?.replyTo?.messageId && onJumpToMessage ? "is-link" : ""}`}
                                        role={message?.replyTo?.messageId && onJumpToMessage ? "button" : undefined}
                                        data-chat-no-select={message?.replyTo?.messageId && onJumpToMessage ? "true" : undefined}
                                        title={message?.replyTo?.messageId && onJumpToMessage ? "Перейти к сообщению" : undefined}
                                        onClick={(event) => {
                                            const targetId = getId(message?.replyTo?.messageId);
                                            if (!targetId || !onJumpToMessage || selectionMode) return;
                                            event.preventDefault();
                                            event.stopPropagation();
                                            onJumpToMessage(targetId);
                                        }}
                                    >
                                        <div className="chat-message-AO__quote-author">{reply.author}</div>
                                        <div className="chat-message-AO__quote-text">{reply.snippet}</div>
                                    </div>
                                )}

                                {isCard && (
                                    <MessageEntityCard
                                        entity={message.entity}
                                        officeId={officeId}
                                    />
                                )}

                                {!!bodyText && !album && (
                                    <div className={`chat-message-AO__text ${inlineMeta ? "has-inline-meta" : ""}`}>
                                        {bodyText}
                                    </div>
                                )}

                                {album ? (
                                    <AlbumGrid
                                        messages={album}
                                        onOpen={(part) => setOpenAttachmentId(part._id)}
                                        onRetry={(part) => onRetry?.(part)}
                                        onTileMenu={selectionMode ? null : (part, x, y) => {
                                            if (canSelectMessage(part)) openActionMenu(part, x, y);
                                        }}
                                    />
                                ) : local ? (
                                    <LocalAttachment message={message} />
                                ) : hasAttachment && (
                                    <Attachment
                                        message={message}
                                        onOpen={() => setOpenAttachmentId(message._id)}
                                    />
                                )}

                                {/* Подпись альбома — под фото, как в Telegram и в приложении. */}
                                {!!bodyText && album && (
                                    <div className="chat-message-AO__text chat-message-AO__album-caption">{bodyText}</div>
                                )}

                                <div
                                    className={[
                                        "chat-message-AO__meta",
                                        inlineMeta ? "is-inline" : "",
                                        overlayMeta ? "is-overlay" : "",
                                        cardOnly ? "is-card-overlay" : "",
                                    ].filter(Boolean).join(" ")}
                                >
                                    <span>{formatTime(message.createdAt)}</span>
                                    {message.editedAt && <span className="chat-message-AO__edited" title="Сообщение изменено">изм.</span>}

                                    {isSelf && sendStatus === "sending" && (
                                        <span
                                            className="chat-message-AO__pending"
                                            title="Отправляется"
                                            aria-label="Отправляется"
                                        >
                                            ◷
                                        </span>
                                    )}

                                    {isSelf && sendStatus === "failed" && (
                                        <span
                                            className="chat-message-AO__send-failed"
                                            title={failedPart?.__sendError || "Не удалось отправить"}
                                            aria-label="Ошибка отправки"
                                        >
                                            !
                                        </span>
                                    )}

                                    {isSelf && !sendStatus && (
                                        <span
                                            className="chat-message-AO__read"
                                            title={readByOther ? "Прочитано" : "Отправлено"}
                                        >
                                            {readByOther ? "✔✔" : "✔"}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Не ушло: причина и кнопки «Повторить» / «Удалить». */}
                            {isSelf && failedPart && (
                                <div className="chat-message-failed-AO" data-chat-no-select="true">
                                    <span>{failedPart.__sendError || "Не отправлено"}</span>
                                    {onRetry && (
                                        <button type="button" onClick={(event) => {
                                            event.stopPropagation();
                                            parts.filter((part) => part?.__sendStatus === "failed").forEach((part) => onRetry(part));
                                        }}>
                                            Повторить
                                        </button>
                                    )}
                                    {onDiscard && (
                                        <button type="button" className="is-danger" onClick={(event) => {
                                            event.stopPropagation();
                                            parts.filter((part) => part?.__sendStatus === "failed").forEach((part) => onDiscard(part));
                                        }}>
                                            Удалить
                                        </button>
                                    )}
                                </div>
                            )}

                            {!!reactions.length && (
                                <div
                                    className={`chat-message-reactions-AO ${isSelf ? "is-self" : "is-other"}`}
                                    data-chat-no-select="true"
                                >
                                    {reactions.map((reaction) => (
                                        <button
                                            key={reaction.emoji}
                                            type="button"
                                            className={myReaction === reaction.emoji ? "is-mine" : ""}
                                            onClick={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                handleReaction(message, reaction.emoji);
                                            }}
                                            title={myReaction === reaction.emoji
                                                ? `Снять реакцию ${reaction.emoji}`
                                                : `Поставить реакцию ${reaction.emoji}`}
                                        >
                                            <span>{reaction.emoji}</span>
                                            <b>{reaction.count}</b>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </Fragment>
                );
                    })}
                </section>
            ))}

            <MessageActionsMenu
                open={!!actionMenu.message}
                message={actionMenu.message}
                isSelf={getId(actionMenu.message?.sender) === getId(currentUserId)}
                anchorPoint={actionMenu.point}
                onClose={closeActionMenu}
                onReply={() => onReply?.(actionMenu.message)}
                onEdit={onEdit && getId(actionMenu.message?.sender) === getId(currentUserId)
                    ? () => onEdit(actionMenu.message)
                    : null}
                onDelete={getId(actionMenu.message?.sender) === getId(currentUserId)
                    ? () => (onDeleteRequest
                        ? onDeleteRequest([actionMenu.message])
                        : onDelete?.(actionMenu.message?._id))
                    : null}
                onDownload={hasSecureChatAttachment(actionMenu.message)
                    ? () => downloadAttachment(actionMenu.message)
                    : null}
                onDownloadAlbum={albumByMessageId.get(getId(actionMenu.message?._id))
                    ? () => albumByMessageId.get(getId(actionMenu.message?._id)).forEach((part) => downloadAttachment(part))
                    : null}
                onForward={() => onForward?.(actionMenu.message)}
                onSelect={() => {
                    const album = albumByMessageId.get(getId(actionMenu.message?._id));
                    (album || [actionMenu.message]).filter(canSelectMessage).forEach((item) => onAddMessageSelection?.(item));
                }}
                onReact={(emoji) => handleReaction(actionMenu.message, emoji)}
                onToTask={TodoFromMessageModal ? () => setTodoMessage(actionMenu.message) : null}
            />

            {todoMessage && TodoFromMessageModal ? (
                <TodoFromMessageModal messages={[todoMessage]} onClose={() => setTodoMessage(null)} />
            ) : null}

            <SiteMediaViewer
                open={!!openAttachmentId && viewableMessages.length > 0}
                items={viewableMessages}
                activeIndex={openAttachmentIndex}
                onActiveIndexChange={handleAttachmentIndexChange}
                onClose={() => setOpenAttachmentId(null)}
                resolveItem={resolveAttachment}
                getItemMimeType={(message) => getChatAttachmentMimeType(message) || "application/octet-stream"}
                getTitle={(message) => getChatAttachmentName(message, "Файл из чата")}
                getSubtitle={(message) => (
                    `${getSenderName(message?.sender, currentUserId, message?.guestSender)} · ${formatTime(message?.createdAt)}`
                )}
                getAlt={(message) => getChatAttachmentName(message, "Файл из чата")}
                onDownload={handleDownload}
                onShare={handleShare}
                shareLabel="Поделиться"
                loadingText="Загружаем защищённый файл…"
                emptyText="Файл недоступен"
            />
        </div>
    );
}
